import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

import page_nav_icon_left from "../../../assets/images/page_nav_icon_left.svg";
import page_nav_icon_right from "../../../assets/images/page_nav_icon_right.svg";
import "./Pagination.css";

const Pagination = ({
  totalItems,
  currentPage,
  itemsPerPage,
  itemsPerPageOptions = [10, 30, 50, 100],
  onPageChange,
  onItemsPerPageChange,
  className,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [hoveredPage, setHoveredPage] = useState(null);
  const selectId = useMemo(
    () => `pg-limit-${Math.random().toString(36).slice(2)}`,
    []
  );

  const getDisplayedPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 2) {
        pages.push("...");
      }
      if (currentPage > 1 && currentPage < totalPages) {
        pages.push(currentPage);
      }
      if (currentPage < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages.filter((page, index) => {
      if (page === "..." && pages[index - 1] === "...") {
        return false;
      }
      return true;
    });
  };

  const pages = getDisplayedPageNumbers();
  const containerClassName = className
    ? `pg-container ${className}`
    : "pg-container";

  return (
    <div className={containerClassName}>
      <div className="pg-limit">
        <label className="pg-limit-label" htmlFor={selectId}>
          Show
        </label>
        <select
          id={selectId}
          className="pg-limit-select"
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      {totalPages > 1 && (
        <nav className="pg-nav" aria-label="Pagination">
          <motion.button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            animate={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            aria-label="Previous page"
          >
            <img
              src={page_nav_icon_left}
              alt="Go left"
              style={{
                objectFit: "none",
                height: "40px",
                width: "35px",
              }}
            />
          </motion.button>
          <div className="pg-pages">
            <AnimatePresence>
              {pages.map((page, index) => (
                <motion.button
                  type="button"
                  key={`${page}-${index}`}
                  onClick={() => page !== "..." && onPageChange(page)}
                  className={`pg-page ${
                    currentPage === page ? "is-active" : ""
                  }`}
                  disabled={page === "..."}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8, position: "absolute" }}
                  transition={{ duration: 0.15 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onHoverStart={() => {
                    if (page !== "...") {
                      setHoveredPage(page);
                    }
                  }}
                  onHoverEnd={() => {
                    setHoveredPage(null);
                  }}
                  aria-current={currentPage === page ? "page" : undefined}
                  aria-label={
                    page === "..." ? "More pages" : `Go to page ${page}`
                  }
                >
                  {(hoveredPage || currentPage) === page && (
                    <motion.div
                      className="pg-active"
                      layoutId="active-page-indicator"
                      transition={{
                        type: "spring",
                        stiffness: 350,
                        damping: 30,
                      }}
                    />
                  )}
                  <span className="pg-text" aria-hidden={page === "..."}>
                    {page}
                  </span>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
          <motion.button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            animate={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            aria-label="Next page"
          >
            <img
              src={page_nav_icon_right}
              alt="Go right"
              style={{
                objectFit: "none",
                height: "40px",
                width: "35px",
              }}
            />
          </motion.button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
